// @ts-check
// =============================================================================
// js/render/image-cache.js — rendered-card caching + art URL resolution.
//
// WHAT THIS IS (cross-cutting.md "Rendering")
//   Card images are expensive to produce (PIXI render of frame + art + text),
//   so the game leans on two caches:
//
//   1. onlineImages — the server pre-renders finished card PNGs to the public
//      GCS bucket; AllCardImagesList.json says which exist. When a card's
//      image is up there we just <img src> it (no client render at all).
//   2. IndexedDB ("imageDB"/"images") — locally rendered card images persist
//      across visits, keyed "<what>_<selectedArt>". Invalidated whenever the
//      server's VERSION (art cache-buster) changes.
//
//   This module also owns SRC(card) — picking WHICH art a card shows and
//   turning it into a bucket URL (port of old client SRC :4116, simplified:
//   the old function had two dead branches that returned the same URL).
//
// WHO USES IT
//   gallery / deck builder / rewards (grids of card images), the duel board,
//   and session.js (VERSION invalidation at login).
// =============================================================================

/** Public art bucket (same one the old client used). */
const BUCKET = 'https://storage.googleapis.com/deckbiter/';

/** Fallback art shown when a card has no resolvable image. */
const GLITCH_URL = BUCKET + 'frames/Glitch.png';

/** Map of pre-rendered image filenames that exist on the bucket. */
const onlineImages = {};

const DB_NAME = 'imageDB';
const STORE_NAME = 'images';

/**
 * Fetch the server-prerendered image list (old client window.onload :140).
 * Call once at boot; safe to fail (we just render locally instead).
 * @returns {Promise<void>}
 */
export async function loadOnlineImageList() {
	try {
		const response = await fetch(BUCKET + 'AllCardImagesList.json?cb=' + Date.now());
		const data = await response.json();
		for (const file of data.uploadedFiles || []) {
			onlineImages[file] = true;
		}
	} catch {
		// Bucket unreachable — every card just renders client-side.
	}
}

/**
 * Is a pre-rendered PNG for this art id available on the bucket?
 * @param {string} artId  e.g. "1_2.png"
 * @returns {boolean}
 */
export function hasOnlineImage(artId) {
	return !!(onlineImages[artId] || onlineImages[String(artId).replace('jpg', 'png')]);
}

/**
 * Resolve which art a card displays and return its bucket URL.
 *
 * Selection order (old SRC): keep card.selectedArt if set; else pick a random
 * collected art; else fall back to the base definition's selected/collected/
 * newarts (first available gender variant in GENDERS priority order).
 *
 * @param {any} card          The card instance to resolve (mutated: selectedArt).
 * @param {any[]} cards       The full card-definition list (for fallbacks).
 * @returns {string}          An absolute image URL (glitch art if unresolvable).
 */
export function SRC(card, cards) {
	try {
		if (!card.selectedArt || card.selectedArt === '0') {
			const base = cards[card.id] || {};
			if (card.collectedArts?.length) {
				card.selectedArt = card.collectedArts[Math.floor(Math.random() * (card.collectedArts.length - 1))];
			} else if (base.selectedArt) {
				card.selectedArt = base.selectedArt;
			} else if (base.collectedArts?.length) {
				base.selectedArt = base.collectedArts[Math.floor(Math.random() * (base.collectedArts.length - 1))];
				card.selectedArt = base.selectedArt;
			} else if (base.newarts?.length) {
				// No ownership data at all: take the first art's first gender
				// variant, preferring generic (GENDERS priority order).
				const priorityKeys = [
					'generic', 'male', 'female', 'futa', 'gyno',
					'male_x_male', 'male_x_female', 'male_x_futa', 'male_x_gyno',
					'female_x_male', 'female_x_female', 'female_x_futa', 'female_x_gyno',
					'futa_x_male', 'futa_x_female', 'futa_x_futa', 'futa_x_gyno',
					'gyno_x_male', 'gyno_x_female', 'gyno_x_futa', 'gyno_x_gyno',
				];
				card.selectedArt = false;
				for (const art of base.newarts) {
					for (const key of priorityKeys) {
						if (art[key]) {
							card.selectedArt = art[key];
							base.selectedArt = card.selectedArt;
							break;
						}
					}
					if (card.selectedArt) break;
				}
			}
		}
	} catch (error) {
		console.error('SRC failed for card:', card, error);
	}

	if (card.selectedArt) {
		return BUCKET + 'Card_' + String(card.selectedArt).replace('.jpg', '.png');
	}
	return GLITCH_URL;
}

// -----------------------------------------------------------------------------
// IndexedDB image cache (old InitDB/LoadImage/SaveImage/ClearObjectStore).
// -----------------------------------------------------------------------------

/** @returns {Promise<IDBDatabase>} */
function openDb() {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, 1);
		request.onupgradeneeded = (event) => {
			/** @type {IDBDatabase} */ (/** @type {any} */ (event.target).result)
				.createObjectStore(STORE_NAME, { keyPath: 'id' });
		};
		request.onerror = () => reject(new Error('imageDB open failed'));
		request.onsuccess = (event) => resolve(/** @type {any} */ (event.target).result);
	});
}

/**
 * Store a rendered image (as a PNG blob) under an id.
 * @param {string} id           Cache key, e.g. "card_12_3.png".
 * @param {Blob} blob           The encoded image data.
 * @returns {Promise<void>}
 */
export async function saveImage(id, blob) {
	const db = await openDb();
	return new Promise((resolve, reject) => {
		const request = db.transaction([STORE_NAME], 'readwrite').objectStore(STORE_NAME).put({ id, blob });
		request.onsuccess = () => resolve();
		request.onerror = () => reject(new Error('imageDB put failed'));
	});
}

/**
 * Load a cached image as an object URL, or null when absent.
 * @param {string} id
 * @returns {Promise<string|null>}  A blob: URL ready for <img src>/PIXI.
 */
export async function loadImage(id) {
	try {
		const db = await openDb();
		return await new Promise((resolve) => {
			const request = db.transaction([STORE_NAME]).objectStore(STORE_NAME).get(id);
			request.onsuccess = () => {
				resolve(request.result?.blob ? URL.createObjectURL(request.result.blob) : null);
			};
			request.onerror = () => resolve(null);
		});
	} catch {
		return null;
	}
}

/**
 * Wipe the whole cache (art VERSION changed).
 * @returns {Promise<void>}
 */
export async function clearImageCache() {
	try {
		const db = await openDb();
		await new Promise((resolve) => {
			const request = db.transaction([STORE_NAME], 'readwrite').objectStore(STORE_NAME).clear();
			request.onsuccess = () => resolve(undefined);
			request.onerror = () => resolve(undefined);
		});
	} catch {
		// No IndexedDB (private mode etc.) — nothing to clear.
	}
}

/**
 * Compare the server's VERSION against the one we cached art under; wipe the
 * cache when they differ (old client :423). Called from session.js at login.
 * @param {string} serverVersion
 * @returns {void}
 */
export function invalidateImageCacheIfStale(serverVersion) {
	const cachedVersion = localStorage.getItem('VERSION');
	if (serverVersion && cachedVersion !== serverVersion) {
		clearImageCache();
	}
	if (serverVersion) {
		localStorage.setItem('VERSION', serverVersion);
	}
}
